import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { AppService } from '@Core/services/proprietary/app.service';
import { DataService } from '@Core/services/proprietary/data.service';
import { Datasource, DataSourceStatus } from '@Core/services/proprietary/datasource';
import { ObjectService } from '@Core/services/proprietary/object.service';
import {
    AddCorrespondOptions,
    AddDocument,
    AddFiling,
    RemoveAllFilingsForDocID,
    RemoveDocument,
    RemoveFiling,
    ResetState,
    SetCorrespondTemplates,
    UpdateCorrespondOptions,
    UpdateDocument,
    UpdateDocumentID,
    UpdateFiling
} from './document-distribute-object.actions';

@Injectable()
export class DocumentDistributeObjectService implements OnDestroy {
    dataService: DataService;
    private dsGetStatusSub: Subscription;
    private dsProcessStatusSub: Subscription;
    dsGet: Datasource;
    dsCorrespondTemplates: Datasource;
    private dsCorrespondTemplatesSub: Subscription;
    callingObjectCode: string;
    constructor(private store: Store, private app: AppService, private objectService: ObjectService) { }

    ngOnDestroy() {
        this.reset();
    }

    //Correspond Templates
    getCorrespondTemplates() {
        // Get the correspond cover templates for the current user
        this.dsCorrespondTemplates.get({
            Procedure_Name: 'epx_getcorrespondtemplatesbyuser',
            UserID: this.app.dsSystemInfo.objectNode.Context.Entity.Entity_ID
        });

        this.dsCorrespondTemplatesSub = this.dsCorrespondTemplates.status$.subscribe((x) => {
            this.getCorrespondTemplatesFromDatasource(x);
        });
    }

    getCorrespondTemplatesFromDatasource(status: DataSourceStatus) {
        // Extract cover template data from dbquery reply
        switch (status) {
            case DataSourceStatus.Ready:
                this.dsCorrespondTemplatesSub.unsubscribe();

                const templates = this.dsCorrespondTemplates.objectNodes;
                let sortedTemplates = templates.sort((a, b) => (a.Code_Desc < b.Code_Desc ? -1 : 1));
                this.setCorrespondTemplates(sortedTemplates);

                break;

            case DataSourceStatus.Error:
                this.dsCorrespondTemplatesSub.unsubscribe();

                break;
        }
    }

    setCorrespondTemplates(templates: any) {
        // Dispatch state action to update templates
        this.store.dispatch(new SetCorrespondTemplates(templates));
    }

    //Links object to the calling windows dataservice
    setDataService(dataService: DataService) {
        this.dataService = dataService;
        this.dsCorrespondTemplates = this.dataService.addNewDatasource('DBQuery');
    }

    resetState() {
        this.store.dispatch(new ResetState());
    }

    reset() {
        if (this.dsGetStatusSub) {
            this.dsGetStatusSub.unsubscribe();
        }
        if (this.dsProcessStatusSub) {
            this.dsProcessStatusSub.unsubscribe();
        }
        if (this.dsCorrespondTemplatesSub) {
            this.dsCorrespondTemplatesSub.unsubscribe();
        }
        if (this.dsCorrespondTemplates) {
            this.dataService.deleteDatasource(this.dsCorrespondTemplates);
        }
        this.resetState();
        this.callingObjectCode = null;
    }

    addDocument(document: any) {
        this.store.dispatch(new AddDocument(document));
    }

    removeDocument(docID: string) {
        this.store.dispatch(new RemoveDocument(docID));
    }

    addFiling(docID: string, filing: any) {
        if (this.callingObjectCode) {
            let objectConfig = this.objectService.getObjectConfig(this.callingObjectCode);
            if (!filing.File_Folder || !filing.File_Drawer) {
                filing.File_Folder = objectConfig?.Filing_Defaults?.File_Folder || '?';
                filing.File_Drawer = objectConfig?.Filing_Defaults?.File_Drawer || '?';
            }
        }
        this.store.dispatch(new AddFiling(docID, filing));
    }

    removeFiling(docID: string, docLinkID: string) {
        this.store.dispatch(new RemoveFiling(docID, docLinkID));
    }

    removeAllFilingsForDocID(docID: string) {
        this.store.dispatch([new RemoveAllFilingsForDocID(docID)]);
    }

    updateDocument(document: any) {
        this.store.dispatch([new UpdateDocument(document)]);
    }

    updateDocumentID(docID: any, newDocID: any) {
        this.store.dispatch([new UpdateDocumentID(docID, newDocID)]);
    }

    updateFiling(filing: any) {
        this.store.dispatch([new UpdateFiling(filing)]);
    }

    addCorrespondOptions(correspondOptions: any) {
        this.store.dispatch([new AddCorrespondOptions(correspondOptions)]);
    }

    updateCorrespondOptions(correspondOptions: any) {
        this.store.dispatch([new UpdateCorrespondOptions(correspondOptions)]);
    }
}
